import { Alert, Stack, Text } from "@mantine/core";
import {
  IconActivity,
  IconChecklist,
  IconClipboardList,
  IconLock,
  IconSettings,
  IconStethoscope,
  IconUsers,
} from "@tabler/icons-react";
import { Role } from "@model/role";
import { SCOPE_TYPE } from "@model";
import { useAuthContext } from "@service";
import { checkRole, getRole } from "@utils/CommonUtil";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  filterTutorialSections,
  TutorialPageShell,
  TutorialSection,
  TutorialSectionCard,
} from "@components/tutorial/TutorialShared";
import { useReleaseNotes } from "@components/versions/useReleaseNotes";
import { desktopTutorialRelease } from "@components/versions/releaseCatalog";

export default function TutorialPage() {
  const { t } = useTranslation("tutorial");
  const { releases, latestOffline } = useReleaseNotes();
  const offlineGuide = desktopTutorialRelease(releases, latestOffline);
  const role = getRole();
  const [query, setQuery] = useState("");
  const { scopes } = useAuthContext();
  const canSeeDistributorTutorial = checkRole([Role.Distributor, Role.Admin]);
  const canSeeAdminTutorial = checkRole([Role.Admin]);
  const canSeeTestSessionTutorial = scopes.includes(SCOPE_TYPE.testSession);

  const relatedLinks = useMemo(
    () =>
      [
        canSeeDistributorTutorial
          ? {
              label: t("tutorialPage.relatedLinks.distributorActions.label"),
              to: "/tutorial/distributor",
              description: t("tutorialPage.relatedLinks.distributorActions.description"),
            }
          : null,
        canSeeAdminTutorial
          ? {
              label: t("tutorialPage.relatedLinks.adminActions.label"),
              to: "/tutorial/admin",
              description: t("tutorialPage.relatedLinks.adminActions.description"),
            }
          : null,
      ].filter(Boolean) as { label: string; to: string; description: string }[],
    [canSeeAdminTutorial, canSeeDistributorTutorial, t],
  );

  const sections: TutorialSection[] = [
    ...(offlineGuide?.tutorial
      ? [
          {
            ...offlineGuide.tutorial,
            icon: <IconSettings size={18} />,
            hideActionArrows: true,
          },
        ]
      : []),
    {
      title: t("tutorialPage.sections.navigation.title"),
      description: t("tutorialPage.sections.navigation.description"),
      icon: <IconChecklist size={18} />,
      actions: [
        {
          title: t("tutorialPage.sections.navigation.actions.newTest.title"),
          location: t("tutorialPage.sections.navigation.actions.newTest.location"),
          steps: t("tutorialPage.sections.navigation.actions.newTest.steps", {
            returnObjects: true,
          }) as string[],
        },
        ...(canSeeTestSessionTutorial
          ? [
              {
                title: t("tutorialPage.sections.navigation.actions.newTestSession.title"),
                location: t("tutorialPage.sections.navigation.actions.newTestSession.location"),
                steps: t("tutorialPage.sections.navigation.actions.newTestSession.steps", {
                  returnObjects: true,
                }) as string[],
              },
              {
                title: t("tutorialPage.sections.navigation.actions.testSessions.title"),
                location: t("tutorialPage.sections.navigation.actions.testSessions.location"),
                steps: t("tutorialPage.sections.navigation.actions.testSessions.steps", {
                  returnObjects: true,
                }) as string[],
              },
            ]
          : []),
        {
          title: t("tutorialPage.sections.navigation.actions.testResults.title"),
          location: t("tutorialPage.sections.navigation.actions.testResults.location"),
          steps: t("tutorialPage.sections.navigation.actions.testResults.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.navigation.actions.patients.title"),
          location: t("tutorialPage.sections.navigation.actions.patients.location"),
          steps: t("tutorialPage.sections.navigation.actions.patients.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.navigation.actions.progression.title"),
          location: t("tutorialPage.sections.navigation.actions.progression.location"),
          steps: t("tutorialPage.sections.navigation.actions.progression.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.navigation.actions.settings.title"),
          location: t("tutorialPage.sections.navigation.actions.settings.location"),
          steps: t("tutorialPage.sections.navigation.actions.settings.steps", {
            returnObjects: true,
          }) as string[],
        },
      ],
    },
    {
      title: t("tutorialPage.sections.newTestPage.title"),
      description: t("tutorialPage.sections.newTestPage.description"),
      icon: <IconStethoscope size={18} />,
      actions: [
        {
          title: t("tutorialPage.sections.newTestPage.actions.deviceStep.title"),
          location: t("tutorialPage.sections.newTestPage.actions.deviceStep.location"),
          steps: t("tutorialPage.sections.newTestPage.actions.deviceStep.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.newTestPage.actions.patientStep.title"),
          location: t("tutorialPage.sections.newTestPage.actions.patientStep.location"),
          steps: t("tutorialPage.sections.newTestPage.actions.patientStep.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.newTestPage.actions.configurationStep.title"),
          location: t("tutorialPage.sections.newTestPage.actions.configurationStep.location"),
          steps: t("tutorialPage.sections.newTestPage.actions.configurationStep.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.newTestPage.actions.optionalToggles.title"),
          location: t("tutorialPage.sections.newTestPage.actions.optionalToggles.location"),
          steps: t("tutorialPage.sections.newTestPage.actions.optionalToggles.steps", {
            returnObjects: true,
          }) as string[],
          note: t("tutorialPage.sections.newTestPage.actions.optionalToggles.note"),
        },
        {
          title: t("tutorialPage.sections.newTestPage.actions.runningTest.title"),
          location: t("tutorialPage.sections.newTestPage.actions.runningTest.location"),
          steps: t("tutorialPage.sections.newTestPage.actions.runningTest.steps", {
            returnObjects: true,
          }) as string[],
        },
      ],
    },
    {
      title: t("tutorialPage.sections.patientsPage.title"),
      description: t("tutorialPage.sections.patientsPage.description"),
      icon: <IconUsers size={18} />,
      actions: [
        {
          title: t("tutorialPage.sections.patientsPage.actions.searchAndClear.title"),
          location: t("tutorialPage.sections.patientsPage.actions.searchAndClear.location"),
          steps: t("tutorialPage.sections.patientsPage.actions.searchAndClear.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.patientsPage.actions.patientRowActions.title"),
          location: t("tutorialPage.sections.patientsPage.actions.patientRowActions.location"),
          steps: t("tutorialPage.sections.patientsPage.actions.patientRowActions.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.patientsPage.actions.editModal.title"),
          location: t("tutorialPage.sections.patientsPage.actions.editModal.location"),
          steps: t("tutorialPage.sections.patientsPage.actions.editModal.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.patientsPage.actions.historyModal.title"),
          location: t("tutorialPage.sections.patientsPage.actions.historyModal.location"),
          steps: t("tutorialPage.sections.patientsPage.actions.historyModal.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.patientsPage.actions.progressionPage.title"),
          location: t("tutorialPage.sections.patientsPage.actions.progressionPage.location"),
          steps: t("tutorialPage.sections.patientsPage.actions.progressionPage.steps", {
            returnObjects: true,
          }) as string[],
        },
      ],
    },
    ...(canSeeTestSessionTutorial
      ? [
          {
            title: t("tutorialPage.sections.runningTestSession.title"),
            description: t("tutorialPage.sections.runningTestSession.description"),
            icon: <IconStethoscope size={18} />,
            actions: [
              {
                title: t("tutorialPage.sections.runningTestSession.actions.createSession.title"),
                location: t(
                  "tutorialPage.sections.runningTestSession.actions.createSession.location",
                ),
                steps: t("tutorialPage.sections.runningTestSession.actions.createSession.steps", {
                  returnObjects: true,
                }) as string[],
              },
              {
                title: t("tutorialPage.sections.runningTestSession.actions.changeConfig.title"),
                location: t(
                  "tutorialPage.sections.runningTestSession.actions.changeConfig.location",
                ),
                steps: t("tutorialPage.sections.runningTestSession.actions.changeConfig.steps", {
                  returnObjects: true,
                }) as string[],
              },
              {
                title: t("tutorialPage.sections.runningTestSession.actions.saveSession.title"),
                location: t(
                  "tutorialPage.sections.runningTestSession.actions.saveSession.location",
                ),
                steps: t("tutorialPage.sections.runningTestSession.actions.saveSession.steps", {
                  returnObjects: true,
                }) as string[],
              },
              {
                title: t("tutorialPage.sections.runningTestSession.actions.virtualAssistant.title"),
                location: t(
                  "tutorialPage.sections.runningTestSession.actions.virtualAssistant.location",
                ),
                steps: t(
                  "tutorialPage.sections.runningTestSession.actions.virtualAssistant.steps",
                  {
                    returnObjects: true,
                  },
                ) as string[],
              },
              {
                title: t("tutorialPage.sections.runningTestSession.actions.runControls.title"),
                location: t(
                  "tutorialPage.sections.runningTestSession.actions.runControls.location",
                ),
                steps: t("tutorialPage.sections.runningTestSession.actions.runControls.steps", {
                  returnObjects: true,
                }) as string[],
              },
            ],
          },
          {
            title: t("tutorialPage.sections.testSessionsPage.title"),
            description: t("tutorialPage.sections.testSessionsPage.description"),
            icon: <IconClipboardList size={18} />,
            actions: [
              {
                title: t("tutorialPage.sections.testSessionsPage.actions.filters.title"),
                location: t("tutorialPage.sections.testSessionsPage.actions.filters.location"),
                steps: t("tutorialPage.sections.testSessionsPage.actions.filters.steps", {
                  returnObjects: true,
                }) as string[],
              },
              {
                title: t("tutorialPage.sections.testSessionsPage.actions.sessionCard.title"),
                location: t("tutorialPage.sections.testSessionsPage.actions.sessionCard.location"),
                steps: t("tutorialPage.sections.testSessionsPage.actions.sessionCard.steps", {
                  returnObjects: true,
                }) as string[],
              },
              {
                title: t("tutorialPage.sections.testSessionsPage.actions.openResults.title"),
                location: t("tutorialPage.sections.testSessionsPage.actions.openResults.location"),
                steps: t("tutorialPage.sections.testSessionsPage.actions.openResults.steps", {
                  returnObjects: true,
                }) as string[],
              },
            ],
          },
        ]
      : []),
    {
      title: t("tutorialPage.sections.resultsPages.title"),
      description: t("tutorialPage.sections.resultsPages.description"),
      icon: <IconClipboardList size={18} />,
      actions: [
        {
          title: t("tutorialPage.sections.resultsPages.actions.resultList.title"),
          location: t("tutorialPage.sections.resultsPages.actions.resultList.location"),
          steps: t("tutorialPage.sections.resultsPages.actions.resultList.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.resultsPages.actions.resultDetail.title"),
          location: t("tutorialPage.sections.resultsPages.actions.resultDetail.location"),
          steps: t("tutorialPage.sections.resultsPages.actions.resultDetail.steps", {
            returnObjects: true,
          }) as string[],
        },
      ],
    },
    {
      title: t("tutorialPage.sections.userSettings.title"),
      description: t("tutorialPage.sections.userSettings.description"),
      icon: <IconSettings size={18} />,
      actions: [
        {
          title: t("tutorialPage.sections.userSettings.actions.accountSection.title"),
          location: t("tutorialPage.sections.userSettings.actions.accountSection.location"),
          steps: t("tutorialPage.sections.userSettings.actions.accountSection.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.userSettings.actions.securitySection.title"),
          location: t("tutorialPage.sections.userSettings.actions.securitySection.location"),
          steps: t("tutorialPage.sections.userSettings.actions.securitySection.steps", {
            returnObjects: true,
          }) as string[],
        },
      ],
    },
    {
      title: t("tutorialPage.sections.organizationPage.title"),
      description: t("tutorialPage.sections.organizationPage.description"),
      icon: <IconSettings size={18} />,
      actions: [
        {
          title: t("tutorialPage.sections.organizationPage.actions.orgDetailButtons.title"),
          location: t("tutorialPage.sections.organizationPage.actions.orgDetailButtons.location"),
          steps: t("tutorialPage.sections.organizationPage.actions.orgDetailButtons.steps", {
            returnObjects: true,
          }) as string[],
          note: t("tutorialPage.sections.organizationPage.actions.orgDetailButtons.note"),
        },
        {
          title: t("tutorialPage.sections.organizationPage.actions.supportingActions.title"),
          location: t("tutorialPage.sections.organizationPage.actions.supportingActions.location"),
          steps: t("tutorialPage.sections.organizationPage.actions.supportingActions.steps", {
            returnObjects: true,
          }) as string[],
        },
      ],
    },
    {
      title: t("tutorialPage.sections.security.title"),
      description: t("tutorialPage.sections.security.description"),
      icon: <IconLock size={18} />,
      actions: [
        {
          title: t("tutorialPage.sections.security.actions.changePassword.title"),
          location: t("tutorialPage.sections.security.actions.changePassword.location"),
          steps: t("tutorialPage.sections.security.actions.changePassword.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.security.actions.enableMfa.title"),
          location: t("tutorialPage.sections.security.actions.enableMfa.location"),
          steps: t("tutorialPage.sections.security.actions.enableMfa.steps", {
            returnObjects: true,
          }) as string[],
          note: t("tutorialPage.sections.security.actions.enableMfa.note"),
        },
      ],
    },
    {
      title: t("tutorialPage.sections.supportInfo.title"),
      description: t("tutorialPage.sections.supportInfo.description"),
      icon: <IconActivity size={18} />,
      actions: [
        {
          title: t("tutorialPage.sections.supportInfo.actions.tutorialPage.title"),
          location: t("tutorialPage.sections.supportInfo.actions.tutorialPage.location"),
          steps: t("tutorialPage.sections.supportInfo.actions.tutorialPage.steps", {
            returnObjects: true,
          }) as string[],
        },
        {
          title: t("tutorialPage.sections.supportInfo.actions.dashboard.title"),
          location: t("tutorialPage.sections.supportInfo.actions.dashboard.location"),
          steps: t("tutorialPage.sections.supportInfo.actions.dashboard.steps", {
            returnObjects: true,
          }) as string[],
        },
      ],
    },
  ];

  const filteredSections = useMemo(
    () => filterTutorialSections(sections, query),
    [sections, query],
  );
  const actionCount = filteredSections.reduce(
    (count, section) => count + section.actions.length,
    0,
  );

  return (
    <TutorialPageShell
      badge={t("tutorialPage.badge")}
      title={t("tutorialPage.title")}
      description={t("tutorialPage.description")}
      relatedLinks={relatedLinks}
      searchValue={query}
      onSearchChange={setQuery}
      resultCount={actionCount}
    >
      <Stack gap="xl">
        <Alert variant="light" color="blue" title={t("tutorialPage.alert.title")}>
          {role === Role.Admin
            ? t("tutorialPage.alert.admin")
            : role === Role.Distributor
              ? t("tutorialPage.alert.distributor")
              : t("tutorialPage.alert.other")}
        </Alert>
        {filteredSections.length === 0 ? (
          <Text c="dimmed">{t("tutorialPage.noResults")}</Text>
        ) : null}
        {filteredSections.map((section) => (
          <TutorialSectionCard key={section.title} section={section} />
        ))}
      </Stack>
    </TutorialPageShell>
  );
}
